import { Alert } from '@mm/components';
import { PlusCircleIcon, EditIcon } from '@mm/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import connectionsCRUDActions, {
  useConnectionCRUDActions,
} from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { showSlideoutMenu } from '@mm/state/actions/viewActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import googleSheetsIcon from 'assets/google-sheets-icon.svg';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import Input from '../../components/Input/Input';
import PrimarySelect from '../../components/PrimarySelect/PrimarySelect';
import { useConnectionWidgetHandlers } from '../../helpers/widget-helpers/use-connection-widget-handlers';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './SendData.pcss';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  isExpanded?: boolean;
  draggableProvided?: DraggableProvided;
  usedInSlideout?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

export const SendDataWidget = ({
  widget,
  draggableProvided,
  usedInSlideout,
  platforms,
}: Props) => {
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const isGoogleSheets = widget.config.kind === 'google_sheets';
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { create, retrieveById } = useConnectionCRUDActions();
  const { connections, googleConnections, connectedPageId } = useSelector(
    (state: StoreState) => ({
      connections: state.connections.widgetConnections,
      googleConnections: state.connections.widgetGoogleConnections,
      connectedPageId: activeFacebookPageIdSelector(state),
    }),
    shallowEqual,
  );

  const [showSearchRightSide, setShowSearchRightSide] = useState(true);
  const accounts = useSelector(selectGoogleAccounts);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const { getOauthUrl } = useGoogleSheetsCRUDActions();
  const [oauthUrl, setOauthUrl] = useState('');

  useEffect(() => {
    if (isGoogleSheets && !accounts.length) {
      const getLink = async () => {
        const link = await getOauthUrl(window.location.href);
        if (link) {
          setOauthUrl(link);
        }
      };
      getLink();
    }
  }, []);

  const addNewGoogleSheetsConnection = useCallback(async () => {
    const connection = await create(activeFbPageId, {
      name: 'Connection',
      description: '',
      kind: ConnectionKind.WIDGET,
      connectionKind: 'google_spreadsheet',
    });
    if (connection) {
      dispatch(
        showSlideoutMenu('googleSheetIntegrationEditor', {
          widgetId: connection.id,
        }),
      );
      onConnectionChange(connection);
      getConnections(connectedPageId);
    }
  }, [activeFbPageId, connectedPageId]);

  const addNewJSONWebhook = useCallback(async () => {
    const connection = await create(activeFbPageId, {
      name: 'Connection',
      description: '',
      connectionType: 'Webhook',
      kind: ConnectionKind.WIDGET,
    });
    if (connection) {
      dispatch(
        showSlideoutMenu('createEditJSONWebhook', {
          connectionIdFromSlideout: connection.id,
          isSlideout: true,
          reloadConnections: () => {
            setTimeout(() => getConnections(connectedPageId), 500);
          },
        }),
      );
      onConnectionChange(connection);
      getConnections(connectedPageId);
    }
  }, [activeFbPageId, connectedPageId]);

  const searchRightSide = useMemo(
    () => (
      <div className="create-edit-button-area__associated-dialogue-input--new-dialogue">
        {showSearchRightSide && (
          <PrimaryButton
            onClick={
              isGoogleSheets ? addNewGoogleSheetsConnection : addNewJSONWebhook
            }
            color="secondary-blue"
            leftIcon={<PlusCircleIcon color="blue" />}
          >
            New Connection
          </PrimaryButton>
        )}
      </div>
    ),
    [showSearchRightSide, addNewGoogleSheetsConnection, addNewJSONWebhook],
  );

  const dispatch = useDispatch();
  const getConnections = (facebookPageId: number) =>
    dispatch(
      connectionsCRUDActions.retrieveWidgetConnections(
        facebookPageId,
        isGoogleSheets ? 'google_spreadsheets' : undefined,
      ),
    );

  useEffect(() => {
    if (connectedPageId) {
      getConnections(connectedPageId);
    }
  }, [connectedPageId]);

  const defaultConnectionConfig = widget.config;
  const [connectionConfig, setConnectionConfig] = useState<
    Partial<SendDataConfig>
  >(defaultConnectionConfig);

  const isUpgradeAlertNeeded =
    !useSelector(combinedFeatureFlagsSelector).connectionWidget &&
    !isGoogleSheets;

  const modalActions = useModalActions();

  const onConnectionChange = (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config) {
      setConnectionConfig(config);
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: isGoogleSheets ? 'google_sheets' : ('zapier' as any),
        },
      };
      connectionWidgetHandlers.update(clonedWidget);
    }
  };

  // connection can be either custom webhook or zap
  const {
    connectionWebhookId: webhookId,
    resourceSubscriptionTriggerName: zapName,
  } = connectionConfig;

  const validGoogleConnections = googleConnections?.filter(
    (connection: ConnectionModel) =>
      connection.active || connection.id === webhookId,
  );
  const connectionsToShow = isGoogleSheets
    ? validGoogleConnections
    : connections;

  const findConnectionByNameOrId = ({ id, name }: ConnectionModel) =>
    webhookId ? id === webhookId : name === zapName;

  const selectedConnection =
    connectionsToShow && connectionsToShow.find(findConnectionByNameOrId);

  const disabledZap =
    !selectedConnection && zapName && `${zapName} (Zap disabled)`;

  const showUpgradeModal = () => {
    modalActions.openUniversalUpgradeLandmineModal({
      feature: 'connectionWidget',
      landmineName: `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}__CONNECTION_WIDGET`,
    });
  };

  const editConnection = useCallback(async () => {
    const connection = await retrieveById(
      activeFbPageId,
      selectedConnection.id,
    );
    const slideoutType = isGoogleSheets
      ? 'googleSheetIntegrationEditor'
      : 'createEditJSONWebhook';
    const params = isGoogleSheets
      ? { widgetId: selectedConnection.id }
      : { connectionIdFromSlideout: selectedConnection.id };

    if (connection) {
      dispatch(
        showSlideoutMenu(slideoutType, {
          ...params,
          isSlideout: true,
          reloadConnections: () => {
            setTimeout(() => getConnections(connectedPageId), 500);
          },
        }),
      );
      onConnectionChange(connection);
    }
  }, [activeFbPageId, connectedPageId, selectedConnection]);

  const heading = isGoogleSheets
    ? 'Google Sheets Integration'
    : 'Zapier Trigger Name/Connection name';

  const classname = classNames('send-data-widget__container', {
    'send-data-widget__container--google-sheets': isGoogleSheets,
  });
  const contentClassname = classNames('send-data-widget__content', {
    'send-data-widget__content--google-sheets': isGoogleSheets,
  });

  return (
    <div className={classname}>
      <div className="send-data-widget__wrapper">
        <div className="send-data-widget__content-container">
          {isGoogleSheets && (
            <img src={googleSheetsIcon} className="send-data-widget__icon" />
          )}
          <div className={contentClassname}>
            <div className="send-data-widget__heading">
              {heading}
              {selectedConnection && !selectedConnection.subscribedResource && (
                <EditIcon
                  onClick={editConnection}
                  className="edit-header__edit-icon"
                />
              )}
            </div>

            <div className="send-data-widget__input-container">
              {connectionsToShow &&
                !disabledZap &&
                ((isGoogleSheets && accounts.length) || !isGoogleSheets) && (
                  <PrimarySelect
                    className="send-data-widget__input-primary-select"
                    dropdownWidth={459}
                    onChange={onConnectionChange}
                    value={selectedConnection}
                    options={connectionsToShow}
                    showSearchFilter={true}
                    searchRightSide={
                      (isGoogleSheets && accounts.length) || !isGoogleSheets ? (
                        searchRightSide
                      ) : (
                        <></>
                      )
                    }
                    closeOnSearchRightSideClick={true}
                    customDropdownStyles={{
                      zIndex: usedInSlideout ? '1000003' : '99',
                    }}
                  />
                )}
              {isGoogleSheets &&
                !accounts.length &&
                hasNativeIntegrationsFeature &&
                oauthUrl && (
                  <div className="integrations__header-info send-data-widget__connect-google-account">
                    <a
                      href={oauthUrl}
                      className="integrations__connect-account"
                    >
                      Connect your account
                    </a>
                    <div className="integrations__header-learn-more">
                      Automatically send data from Customers.ai to Google
                      Sheets.
                    </div>
                  </div>
                )}
              {disabledZap && <Input readonly="readonly" value={disabledZap} />}
            </div>
          </div>
        </div>

        {isUpgradeAlertNeeded && (
          <Alert className="send-data-widget__alert" showCloseIcon={false}>
            You no longer have access to this Startup feature. To continue using
            this widget,{' '}
            <span
              className="send-data-widget__alert-link"
              onClick={showUpgradeModal}
            >
              Upgrade to Startup
            </span>
          </Alert>
        )}
      </div>
      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    </div>
  );
};
